import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BrandDot } from "@/components/util/BrandDot";

/**
 * Shared page hero — centered eyebrow + heading + description block used
 * above the card grid on every catalog-style page (/services, /laptops,
 * /portfolio, /software). Single source of truth for the header height
 * and spacing so pages can't drift from each other.
 *
 * Optional CTA button (Phase 8.2): pass `ctaLabel` + `ctaLink` to render
 * a primary button below the description. When `ctaLabel` is empty the
 * button doesn't render at all.
 *
 * Brand-dot accents (Phase 8.3):
 *   - `accentInline` (bool): when true, the orange accent renders
 *     immediately adjacent to the primary heading text with no space
 *     (for trailing punctuation like the "Ur Designs." dot). Default
 *     spaced behavior preserved when false.
 *   - `showDot` (bool): renders a standalone <BrandDot> divider between
 *     the heading and description. Independent of accentInline.
 *
 * Props:
 *   eyebrow      required — small orange caps label
 *   plainEyebrow optional — render eyebrow as simple orange uppercase
 *                text (no outlined `.ur-eyebrow` frame). Deliberate
 *                page-specific exception, used on /software where the
 *                product cards already show many outlined vendor labels
 *                (ADOBE, MICROSOFT, AUTODESK …) and an additional page
 *                frame would read as visual repetition.
 *   heading      required — main title text (can include <span>)
 *   accent       optional — accent-color span appended to heading
 *   accentInline optional — attach accent with no space (for trailing "." etc.)
 *   showDot      optional — render a standalone brand-dot divider
 *   description  optional — supporting paragraph under heading
 *   mobileDescription optional — shorter paragraph shown on <lg viewports.
 *                     When populated it replaces `description` on mobile
 *                     ONLY (desktop keeps the long-form copy verbatim).
 *                     Empty/null → mobile falls back to `description`.
 *                     Used to give /services, /software and /workstations
 *                     tighter mobile heroes without duplicating the
 *                     page-settings model or affecting desktop.
 *   ctaLabel     optional — button text; empty/undefined = no button
 *   ctaLink      optional — button destination (internal path or full URL)
 *   testId       optional — root data-testid
 */
export const PageHero = ({ eyebrow, plainEyebrow, heading, accent, accentInline, showDot, description, mobileDescription, ctaLabel, ctaLink, testId }) => {
  const showCta = !!(ctaLabel && ctaLabel.trim());
  const linkTarget = ctaLink || "#";
  const isExternal = /^https?:\/\//.test(linkTarget);

  // Same-path / empty-link CTA handler. If the CTA has no meaningful
  // destination (empty link, "#", or the page it's already on — e.g.
  // CMS pointed the /workstations hero CTA at "/workstations"), a raw
  // <Link to="..."> click is a visual no-op. In those cases we
  // intercept the click and smooth-scroll the visitor to the section
  // directly below the hero — the intended forward motion — so the
  // button remains a useful, working CTA without requiring a CMS
  // change or a new route. External links and links to other pages /
  // in-page anchors are untouched.
  const handleCtaClick = (e) => {
    if (isExternal) return;
    const isNoop =
      !linkTarget ||
      linkTarget === "#" ||
      (() => {
        try {
          const u = new URL(linkTarget, window.location.origin);
          return u.pathname === window.location.pathname && !u.hash;
        } catch {
          return false;
        }
      })();
    if (!isNoop) return;
    e.preventDefault();
    const hero = e.currentTarget.closest("section");
    const next = hero && hero.nextElementSibling;
    if (next && typeof next.scrollIntoView === "function") {
      next.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollBy({ top: window.innerHeight * 0.8, behavior: "smooth" });
    }
  };
  return (
    <section
      data-testid={testId || "page-hero"}
      className="relative bg-[#0F0F0F] overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-24 right-0 w-[520px] h-[520px] rounded-full opacity-20 blur-[120px] pointer-events-none"
        style={{ background: "radial-gradient(circle, #FF6600 0%, transparent 60%)" }}
      />
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-12 sm:pt-14 pb-6">
        <div className="max-w-3xl mx-auto text-center">
          {plainEyebrow ? (
            <div
              data-testid={`${testId || "page-hero"}-eyebrow-plain`}
              className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#FF6600]"
            >
              {eyebrow}
            </div>
          ) : (
            <div className="ur-eyebrow">
              {eyebrow}
            </div>
          )}
          <h1 className="font-display mt-4 sm:mt-5 text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] text-white">
            {accentInline ? (
              <>
                {heading}
                {accent && <span className="text-[#FF6600]">{accent}</span>}
              </>
            ) : (
              <>
                <span className="inline">{heading}</span>
                {accent && (
                  <>
                    {/* Non-inline accent: render on its own visual row so
                        the two coloured strings never collide, regardless
                        of JSX whitespace collapsing. */}
                    <span className="block sm:inline sm:ml-3 text-[#FF6600]">
                      {accent}
                    </span>
                  </>
                )}
              </>
            )}
          </h1>
          {showDot && <BrandDot testId={`${testId || "page-hero"}-dot`} />}
          {(() => {
            // Responsive description: when a mobile-specific copy is
            // provided by the CMS render the short form on <lg and
            // the long form on lg+; otherwise fall back to a single
            // description on every viewport.
            const mob = (mobileDescription || "").trim();
            if (mob && description) {
              return (
                <>
                  <p
                    data-testid={`${testId || "page-hero"}-description-mobile`}
                    className="mt-3 text-[#B5B5B5] text-base max-w-2xl mx-auto leading-relaxed lg:hidden"
                  >
                    {mob}
                  </p>
                  <p
                    data-testid={`${testId || "page-hero"}-description`}
                    className="mt-3 text-[#B5B5B5] text-lg max-w-2xl mx-auto leading-relaxed hidden lg:block"
                  >
                    {description}
                  </p>
                </>
              );
            }
            const single = mob || description;
            if (!single) return null;
            return (
              <p
                data-testid={`${testId || "page-hero"}-description`}
                className="mt-3 text-[#B5B5B5] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed"
              >
                {single}
              </p>
            );
          })()}
          {showCta && (
            <div className="mt-6">
              {isExternal ? (
                <a
                  href={linkTarget}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`${testId || "page-hero"}-cta`}
                  className="ur-btn-primary"
                >
                  {ctaLabel} <ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <Link
                  to={linkTarget}
                  onClick={handleCtaClick}
                  data-testid={`${testId || "page-hero"}-cta`}
                  className="ur-btn-primary"
                >
                  {ctaLabel} <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
